<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-4 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-3">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-3">
              <b-icon-graph-up class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0 header-title">SALES REPORT</h5>
              <small class="header-subtitle">Real-time analytics & performance metrics</small>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS ROW -->
    <b-card class="mb-4 filters-card" no-body>
      <b-card-body class="py-3">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 16px;">
              <div style="min-width: 320px;">
                <label class="small text-muted mb-2 filter-label">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="SELECT DATE RANGE"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 8px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="md"
                  :variant="picked_date_range === btn.id ? 'primary' : 'outline-primary'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-2 px-3"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>

          <div class="col-lg-4 mt-3 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="primary"
                @click="fetchSummaryData"
                class="search-btn"
                :disabled="isLoading"
              >
                <b-icon-search class="mr-2" />
                <span v-if="!isLoading">ANALYZE</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-2" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-3">
          <b-button
            variant="link"
            size="md"
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none advanced-toggle"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>

          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-3 pt-3 border-top">
              <div class="row">
                <div class="col-md-3 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    :options="locationOptions"
                    v-model="selectedLocation"
                    size="md"
                    class="filter-select"
                    @change="onLocationChange"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null">ALL LOCATIONS</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-person class="mr-1" size="sm" />SALESPERSON
                  </label>
                  <b-form-select
                    :options="salespersonOptions"
                    v-model="selectedSalesperson"
                    size="md"
                    class="filter-select"
                    @change="onSalespersonChange"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null">ALL SALESPEOPLE</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-3">
                  <label class="small text-muted mb-2 filter-label">
                    <b-icon-tag class="mr-1" size="sm" />SALE TYPE
                  </label>
                  <b-form-select
                    :options="saleTypeOptions"
                    v-model="selectedSaleType"
                    size="md"
                    class="filter-select"
                    @change="onFilterChange"
                  ></b-form-select>
                </div>

                <div class="col-md-3 mb-3">
                  <div class="mt-1">
                    <b-form-radio-group
                      v-model="viewMode"
                      size="md"
                      buttons
                      button-variant="outline-secondary"
                      @change="onViewModeChange"
                    >
                      <b-form-radio value="summary">Summary View</b-form-radio>
                      <b-form-radio value="detailed">Detailed View</b-form-radio>
                    </b-form-radio-group>
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" type="grow" label="Loading..." style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 text-muted" style="font-size: 18px;">Fetching sales data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="summaryData && summaryData.total_sales > 0">
      <div class="mb-4">
        <!-- KPI CARDS -->
        <div class="kpi-grid">
          <div class="kpi-card dark accent-green">
            <div class="kpi-icon-wrap icon-green">
              <b-icon-cash-stack />
            </div>
            <span class="kpi-label">Total Sales</span>
            <span class="kpi-value">{{ formatCurrency(summaryData.total_sales) }}</span>
            <div class="kpi-trend trend-green">
              <b-icon-graph-up-arrow />
              {{ summaryData.total_orders }} orders
            </div>
          </div>

          <div class="kpi-card accent-blue">
            <div class="kpi-icon-wrap icon-blue">
              <b-icon-cart-plus />
            </div>
            <span class="kpi-label">Regular Sales</span>
            <span class="kpi-value">{{ formatCurrency(summaryData.regular_sales) }}</span>
            <div class="kpi-trend trend-blue">
              <b-icon-graph-up-arrow />
              {{ ((summaryData.regular_sales / summaryData.total_sales) * 100).toFixed(1) }}% of total
            </div>
          </div>

          <div class="kpi-card accent-purple">
            <div class="kpi-icon-wrap icon-purple">
              <b-icon-wallet />
            </div>
            <span class="kpi-label">Net Sales</span>
            <span class="kpi-value">{{ formatCurrency(summaryData.total_net_sales) }}</span>
            <div class="kpi-trend trend-purple">
              <b-icon-check-circle />
              Regular + Layaway − Refunds
            </div>
          </div>

          <div class="kpi-card accent-amber">
            <div class="kpi-icon-wrap icon-amber">
              <b-icon-clock-history />
            </div>
            <span class="kpi-label">Layaway</span>
            <span class="kpi-value">{{ formatCurrency(summaryData.layaway_sales) }}</span>
            <div class="kpi-trend trend-amber">
              <b-icon-cash />
              Paid: {{ formatCurrency(summaryData.total_paid_layaway) }}
            </div>
          </div>

          <div class="kpi-card accent-red">
            <div class="kpi-icon-wrap icon-red">
              <b-icon-arrow-return-left />
            </div>
            <span class="kpi-label">Refunds</span>
            <span class="kpi-value">{{ formatCurrency(summaryData.total_refund) }}</span>
            <div class="kpi-trend" :class="refundRate < 5 ? 'trend-green' : 'trend-red'">
              <b-icon-graph-down-arrow v-if="refundRate >= 5" />
              <b-icon-graph-up-arrow v-else />
              {{ refundRate }}% of total
            </div>
          </div>

          <div v-if="isAdmin" class="kpi-card accent-emerald">
            <div class="kpi-icon-wrap icon-emerald">
              <b-icon-graph-up />
            </div>
            <span class="kpi-label">Commission</span>
            <span class="kpi-value">{{ formatCurrency(summaryData.total_commission) }}</span>
            <div class="kpi-trend trend-emerald">
              <b-icon-percent />
              {{ commissionRate }}% rate
            </div>
          </div>
        </div>

        <!-- CHARTS SECTION -->
        <div class="row mb-3">
          <div class="col-lg-7 mb-3">
            <b-card no-body class="chart-card">
              <b-card-header class="bg-white border-bottom py-2">
                <div class="d-flex align-items-center">
                  <div class="chart-header-icon mr-2">
                    <b-icon-bar-chart-line />
                  </div>
                  <h6 class="mb-0 chart-title">Daily Sales & Refunds Trend</h6>
                </div>
              </b-card-header>
              <b-card-body class="p-3">
                <div class="chart-container" style="height: 340px;">
                  <canvas ref="dailyTrendChart"></canvas>
                </div>
              </b-card-body>
            </b-card>
          </div>
          <div class="col-lg-5 mb-3">
            <b-card no-body class="chart-card">
              <b-card-header class="bg-white border-bottom py-2">
                <div class="d-flex align-items-center">
                  <div class="chart-header-icon mr-2">
                    <b-icon-pie-chart />
                  </div>
                  <h6 class="mb-0 chart-title">Sales by Location</h6>
                </div>
              </b-card-header>
              <b-card-body class="p-3">
                <div class="pie-chart-container" style="height: 320px;">
                  <canvas ref="locationChart"></canvas>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>

        <div v-if="selectedLocation || selectedSalesperson" class="mb-3">
          <b-alert show variant="info" class="py-2 filter-alert">
            <b-icon-info-circle class="mr-2" size="sm" />
            Showing data for
            <strong v-if="selectedLocation && selectedSalesperson">
              {{ getLocationName(selectedLocation) }} - {{ selectedSalesperson }}
            </strong>
            <strong v-else-if="selectedLocation">
              {{ getLocationName(selectedLocation) }}
            </strong>
            <strong v-else-if="selectedSalesperson">
              {{ selectedSalesperson }}
            </strong>
            <b-button variant="link" size="md" @click="clearFilters" class="p-0 ml-2 clear-filter-btn">Clear Filters</b-button>
          </b-alert>
        </div>

        <div class="" style="align-items: center; display: flex; justify-content: right;">
          <div class="mt-1">
            <b-form-radio-group
              v-model="viewMode"
              size="md"
              buttons
              button-variant="outline-secondary"
              @change="onViewModeChange"
            >
              <b-form-radio value="summary">Summary View</b-form-radio>
              <b-form-radio value="detailed">Detailed View</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <!-- SUMMARY TABLES -->
        <div class="row mb-3">
          <div class="col-lg-6 mb-3">
            <b-card no-body class="data-table-card h-100">
              <b-card-header class="bg-light py-2">
                <div class="d-flex align-items-center">
                  <b-icon-building class="mr-2" size="sm" />
                  <h6 class="mb-0 table-title">Performance by Location</h6>
                  <b-badge variant="light" class="ml-2 table-badge">
                    <b-icon-mouse2 size="sm" class="mr-1" />Click row for details
                  </b-badge>
                </div>
              </b-card-header>
              <b-card-body class="p-0">
                <div class="table-wrapper-sm" style="max-height: 400px;">
                  <table class="compact-table">
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th class="text-right">Regular ($)</th>
                        <th class="text-right">Layaway ($)</th>
                        <th class="text-right">Layaway Paid ($)</th>
                        <th class="text-right">Refunds ($)</th>
                        <th class="text-right">Net ($)</th>
                        <th class="text-right">Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(data, locationId) in locationSummary"
                        :key="locationId"
                        :class="{ 'highlight-row': selectedLocation == locationId }"
                        @click="selectLocationFromTable(locationId)"
                      >
                        <td>{{ getLocationName(locationId) }}</td>
                        <td class="text-right">{{ formatCurrency(data.regular_sales || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(data.layaway_total || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(data.layaway_paid || 0) }}</td>
                        <td class="text-right text-danger">{{ formatCurrency(data.refund || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(data.net || 0) }}</td>
                        <td class="text-right">{{ data.count || 0 }}</td>
                      </tr>
                    </tbody>
                    <tfoot class="bg-light font-weight-bold">
                      <tr>
                        <td>TOTAL</td>
                        <td class="text-right">{{ formatCurrency(totalLocationRegularSales) }}</td>
                        <td class="text-right">{{ formatCurrency(totalLocationLayawayTotal) }}</td>
                        <td class="text-right">{{ formatCurrency(totalLocationLayawayPaid) }}</td>
                        <td class="text-right">{{ formatCurrency(totalLocationRefunds) }}</td>
                        <td class="text-right">{{ formatCurrency(totalLocationNet) }}</td>
                        <td class="text-right">{{ totalLocationOrders }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </b-card-body>
            </b-card>
          </div>

          <div class="col-lg-6 mb-3">
            <b-card no-body class="data-table-card h-100">
              <b-card-header class="bg-light py-2">
                <div class="d-flex align-items-center">
                  <b-icon-people class="mr-2" size="sm" />
                  <h6 class="mb-0 table-title">Performance by Salesperson</h6>
                  <b-badge variant="light" class="ml-2 table-badge">
                    <b-icon-mouse2 size="sm" class="mr-1" />Click row for details
                  </b-badge>
                </div>
              </b-card-header>
              <b-card-body class="p-0">
                <div class="table-wrapper-sm" style="max-height: 400px;">
                  <table class="compact-table">
                    <thead>
                      <tr>
                        <th>Salesperson</th>
                        <th class="text-right">Regular ($)</th>
                        <th class="text-right">Layaway ($)</th>
                        <th class="text-right">Layaway Paid ($)</th>
                        <th class="text-right">Refunds ($)</th>
                        <th class="text-right">Net ($)</th>
                        <th class="text-right">Orders</th>
                        <th class="text-right">Avg Order ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(data, salesperson) in salespersonSummary"
                        :key="salesperson"
                        :class="{ 'highlight-row': selectedSalesperson == salesperson }"
                        @click="selectSalespersonFromTable(salesperson)"
                      >
                        <td>{{ salesperson }}</td>
                        <td class="text-right">{{ formatCurrency(data.regular_sales || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(data.layaway_total || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(data.layaway_paid || 0) }}</td>
                        <td class="text-right text-danger">{{ formatCurrency(data.refund || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(data.net || 0) }}</td>
                        <td class="text-right">{{ data.count || 0 }}</td>
                        <td class="text-right">{{ formatCurrency((data.net || 0) / ((data.count || 1))) }}</td>
                      </tr>
                    </tbody>
                    <tfoot class="bg-light font-weight-bold">
                      <tr>
                        <td>TOTAL</td>
                        <td class="text-right">{{ formatCurrency(totalSalespersonRegularSales) }}</td>
                        <td class="text-right">{{ formatCurrency(totalSalespersonLayawayTotal) }}</td>
                        <td class="text-right">{{ formatCurrency(totalSalespersonLayawayPaid) }}</td>
                        <td class="text-right">{{ formatCurrency(totalSalespersonRefunds) }}</td>
                        <td class="text-right">{{ formatCurrency(totalSalespersonNet) }}</td>
                        <td class="text-right">{{ totalSalespersonOrders }}</td>
                        <td class="text-right">{{ formatCurrency(totalSalespersonAvgOrder) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>

        <!-- DETAILED DATA LOADING -->
        <div v-if="isLoadingDetailed" class="text-center py-3">
          <b-spinner variant="primary" size="md" class="mr-2"></b-spinner>
          <small style="font-size: 16px;">Loading detailed transactions...</small>
        </div>

        <!-- ===================================================== -->
        <!-- DETAILED TRANSACTIONS TABLE (COMPACT - NO H-SCROLL)   -->
        <!-- ===================================================== -->
        <div v-if="viewMode === 'detailed'" class="mt-4">
          <b-card no-body class="data-table-card">
            <b-card-header class="bg-light d-flex justify-content-between align-items-center py-2">
              <div class="d-flex align-items-center">
                <b-icon-table class="mr-2" size="sm" />
                <h6 class="mb-0 table-title">
                  Detailed Transactions
                  <span v-if="selectedLocation && selectedSalesperson"> for {{ getLocationName(selectedLocation) }} - {{ selectedSalesperson }}</span>
                  <span v-else-if="selectedLocation"> for {{ getLocationName(selectedLocation) }}</span>
                  <span v-else-if="selectedSalesperson"> for {{ selectedSalesperson }}</span>
                </h6>
                <b-badge variant="secondary" class="ml-2 table-badge">{{ filteredDetailedData.length }} records</b-badge>
              </div>
              <div class="d-flex align-items-center">
                <b-button
                  size="md"
                  :variant="showWarrantyWithDiscount ? 'warning' : 'outline-warning'"
                  @click="showWarrantyWithDiscount = !showWarrantyWithDiscount"
                  class="mr-2"
                  :title="showWarrantyWithDiscount ? 'Click to clear filter' : 'Show only invoices with warranty + discount'"
                >
                  <b-icon-shield-check class="mr-1" size="sm" />
                  Warranty + Discount
                  <b-badge v-if="showWarrantyWithDiscount" variant="dark" class="ml-1">
                    {{ filteredDetailedData.length }}
                  </b-badge>
                </b-button>
                <b-button size="md" variant="outline-secondary" @click="exportToCSV" class="mr-2">
                  <b-icon-download class="mr-1" size="sm" /> Export
                </b-button>
                <b-button size="md" variant="outline-primary" @click="refreshDetailedData">
                  <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
                </b-button>
              </div>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="compact-detail-wrapper">
                <table class="sales-table compact-sales-table">
                  <thead>
                    <tr style="background: #c3cdd3;">
                      <th class="col-date"     @click="sortDetailedBy('ts')">DATE</th>
                      <th class="col-loc"      @click="sortDetailedBy('location_id')">SOLD FROM</th>
                      <th class="col-inv"      @click="sortDetailedBy('invoice_id')">INV #</th>
                      <th class="col-by"       @click="sortDetailedBy('username')">SOLD BY</th>
                      <th style ="background-color: lightblue;"class="col-num"      @click="sortDetailedBy('order_total')">ORDER TOTAL</th>
                      <th style ="background-color: lightblue;"class="col-num-sm"   @click="sortDetailedBy('total_discount')">DISC</th>
                      <th style ="background-color: lightblue;" class="col-num-sm"   @click="sortDetailedBy('discount_percentage')">DISC %</th>
                      <th style ="background-color: lightblue;"class="col-num"      @click="sortDetailedBy('base_commission')">S COMM</th>
                      <th style ="background-color: lightblue;"class="col-num"      @click="sortDetailedBy('extra_charges')">EX CHARGES</th>
                      <th style ="background-color: lightblue;"class="col-num-sm"   @click="sortDetailedBy('sales_tax')">SLS TX</th>
                      <th style ="background-color: lightblue;"class="col-num"      @click="sortDetailedBy('total_dollar_amount')">INVOICE TOTAL</th>
                      <th style ="background-color: orange;"class="col-num-sm"   @click="sortDetailedBy('delivery_charges')">DLVRY</th>
                      <th style ="background-color: orange;"class="col-num-sm"   @click="sortDetailedBy('delivery_commission')">D COMM</th>
                         <th style ="background-color: lightgreen;"class="col-num-sm"   @click="sortDetailedBy('setup_charges')">SETUP</th>
                      <th style ="background-color: lightgreen;" class="col-num-sm"   @click="sortDetailedBy('setup_commission')">S COMM</th>
                      <th style ="background-color: yellow;"class="col-num-sm"   @click="sortDetailedBy('warranty_charges')">WRNTY</th>
                      <th style ="background-color: yellow;"class="col-num-sm"   @click="sortDetailedBy('warranty_commission')">W COMM</th>
                     
                   
                      <th style ="background-color: lightpink;"class="col-num-sm"   @click="sortDetailedBy('spiff_commission')">SPIFF</th>
                      <th style ="background-color: lightpink;"class="col-num-sm"   @click="sortDetailedBy('total_commission')">T COMM</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Totals Row -->
                    <tr style="background: lightyellow;">
                      <td class="header" colspan="4" style="text-align: right;"><strong>TOTALS:</strong></td>
                      <td class="header"><strong>${{ totalDetailedOrderTotal.toFixed(2) }}</strong></td>
                      <td class="header">${{ totalDetailedDiscount.toFixed(2) }}</td>
                      <td class="header">{{ totalDetailedDiscountPercent.toFixed(2) }}%</td>
                      <td class="header"><strong>${{ totalDetailedBaseCommission.toFixed(2) }}</strong></td>
                      <td class="header">${{ totalDetailedExtraCharges.toFixed(2) }}</td>
                      <td class="header">${{ totalDetailedTax.toFixed(2) }}</td>
                      <td class="header"><strong>${{ totalDetailedSales.toFixed(2) }}</strong></td>
                           <td class="header">${{ totalDetailedDelivery.toFixed(2) }}</td>
                      <td class="header">${{ totalDetailedDeliveryComm.toFixed(2) }}</td>
                      
                      <td class="header">${{ totalDetailedSetup.toFixed(2) }}</td>
                      <td class="header">${{ totalDetailedSetupComm.toFixed(2) }}</td>
                      <td class="header">${{ totalDetailedWarranty.toFixed(2) }}</td>
                      <td class="header">${{ totalDetailedWarrantyComm.toFixed(2) }}</td>
                 
                      <td class="header">${{ totalDetailedSpiff.toFixed(2) }}</td>
                      <td class="header"><strong>${{ totalDetailedTotalCommission.toFixed(2) }}</strong></td>
                    </tr>

                    <!-- Data Rows -->
                    <tr v-for="(item, idx) in paginatedDetailedData" :key="idx"
                        :style="{ background: getRowBackgroundColor(item), fontWeight: calculateDiscountPercent(item) > 5 ? 'bold' : 'normal' }">
                      <td class="non-header">{{ formatDateTime(item.ts) }}</td>
                      <td class="non-header">{{ getLocationName(item.location_id) }}</td>
                      <td class="non-header">
                        <a
                          href="#"
                          @click.prevent="openInvoice(item.invoice_id)"
                          class="invoice-link"
                          :title="'Open invoice ' + item.invoice_id"
                        >
                          {{ item.invoice_id }}
                        </a>
                      </td>
                      <td class="non-header">{{ item.username }}</td>
                      <td class="non-header text-right">${{ (item.order_total || 0).toFixed(2) }}</td>
                      <td class="non-header text-right">${{ (item.total_discount || 0).toFixed(2) }}</td>
                      <td class="non-header text-right" :style="{ backgroundColor: calculateDiscountPercent(item) > 5 ? '#ffcccc' : 'transparent', fontWeight: 'bold' }">
                        {{ calculateDiscountPercent(item).toFixed(2) }}%
                      </td>
                      <td class="non-header text-right">${{ adjustedBaseCommission(item).toFixed(2) }}</td>
                      <td class="non-header text-right">${{ extraCharges(item).toFixed(2) }}</td>
                      <td class="non-header text-right">${{ (item.sales_tax || 0).toFixed(2) }}</td>
                      <td class="non-header text-right" :class="{ 'text-danger': item.total_dollar_amount < 0 }">
                        ${{ (item.total_dollar_amount || 0).toFixed(2) }}
                      </td>
                        <td class="non-header text-right">${{ (item.delivery_charges || 0).toFixed(2) }}</td>
                      <td class="non-header text-right">${{ adjustedDeliveryCommission(item).toFixed(2) }}</td>
                          <td class="non-header text-right">${{ (item.setup_charges || 0).toFixed(2) }}</td>
                      <td class="non-header text-right">${{ adjustedSetupCommission(item).toFixed(2) }}</td>
                      <td class="non-header text-right">${{ (item.warranty_charges || 0).toFixed(2) }}</td>
                      <td class="non-header text-right">${{ adjustedWarrantyCommission(item).toFixed(2) }}</td>
                    
                  
                      <td class="non-header text-right">${{ (item.spiff_commission || 0).toFixed(2) }}</td>
                      <td class="non-header text-right">
                        <strong>${{ adjustedTotalCommission(item).toFixed(2) }}</strong>
                      </td>
                    </tr>
                    <tr v-if="filteredDetailedData.length === 0 && !isLoadingDetailed">
                      <td colspan="19" class="text-center py-4 text-muted" style="font-size: 18px;">
                        No transactions match the selected filters
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div v-if="filteredDetailedData.length > 0" class="pagination-wrapper d-flex justify-content-between align-items-center p-3 bg-light">
                <small class="text-muted" style="font-size: 16px;">
                  Showing {{ (detailedPage - 1) * detailedPageSize + 1 }} to {{ Math.min(detailedPage * detailedPageSize, filteredDetailedData.length) }} of {{ filteredDetailedData.length }}
                </small>
                <b-pagination
                  v-model="detailedPage"
                  :total-rows="filteredDetailedData.length"
                  :per-page="detailedPageSize"
                  size="md"
                  class="mb-0"
                  align="center"
                ></b-pagination>
                <b-form-select v-model="detailedPageSize" size="md" style="width: 85px;" @change="detailedPage = 1">
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </b-form-select>
              </div>
            </b-card-body>
          </b-card>
        </div>

        <!-- Messages -->
        <div
          v-if="viewMode === 'summary' && !(selectedLocation || selectedSalesperson)"
          class="text-center py-4 bg-light rounded"
        >
          <b-icon-eye-slash class="mr-2" size="sm" />
          <small class="text-muted" style="font-size: 16px;">Summary View: Click any row in the tables above to switch to Detailed View</small>
        </div>

        <div
          v-if="viewMode === 'summary' && (selectedLocation || selectedSalesperson)"
          class="text-center py-4 bg-info rounded text-white"
        >
          <b-icon-info-circle class="mr-2" size="sm" />
          <small style="font-size: 16px;">Summary View: Detailed transactions are hidden. Switch to "Detailed View" above to see transaction-level data.</small>
        </div>

        <div
          v-if="viewMode === 'detailed' && !(selectedLocation || selectedSalesperson)"
          class="text-center py-4 bg-light rounded"
        >
          <b-icon-filter-circle class="mr-2" size="sm" />
          <small class="text-muted" style="font-size: 16px;">Detailed View: Select a location or salesperson from the tables above to view detailed transactions</small>
        </div>
      </div>
    </template>

    <!-- NO DATA STATE -->
    <div v-else-if="!isLoading && !summaryData" class="text-center py-5">
      <b-alert show variant="light" class="mb-0" style="font-size: 18px;">
        <b-icon-search class="mr-2" />
        Select a date range and click ANALYZE to view sales intelligence
      </b-alert>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import Chart from 'chart.js';
import axios from 'axios';

export default {
  name: "SalesDashboardOptimized",
  components: {
    DatePicker,
  },
  data() {
    return {
      packet: {
        userDetails: {},
        locations: []
      },

      isLoading: false,
      isLoadingDetailed: false,
      showAdvancedFilters: false,
      viewMode: 'summary',

      showWarrantyWithDiscount: false,

      summaryData: null,
      locationSummary: {},
      salespersonSummary: {},
      dailyTrend: {},

      detailedData: [],

      date_range: [],
      picked_date_range: null,
      selectedLocation: null,
      selectedSalesperson: null,
      selectedSaleType: null,
      dateRangeFocused: false,

      detailedSortKey: 'ts',
      detailedSortAsc: false,
      detailedPage: 1,
      detailedPageSize: 25,

      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],

      saleTypeOptions: [
        { value: null, text: 'ALL TYPES' },
        { value: 'regular', text: 'REGULAR' },
        { value: 'layaway', text: 'LAYAWAY' },
        { value: 'refund', text: 'REFUND' }
      ],

      dailyTrendChart: null,
      locationChart: null,

      chartColors: {
        primary: '#6366f1',
        success: '#10b981',
        warning: '#f59e0b',
        danger: '#ef4444',
        info: '#06b6d4',
        purple: '#8b5cf6'
      }
    };
  },
  computed: {
    formattedDateRange() {
      if (!this.date_range || this.date_range.length < 2) return 'Select dates';
      return `${this.date_range[0]} - ${this.date_range[1]}`;
    },

    isAdmin() {
      return this.packet.userDetails.userrole === 'admin';
    },

    commissionRate() {
      if (!this.summaryData || this.summaryData.total_sales === 0) return 0;
      return ((this.summaryData.total_commission / this.summaryData.total_sales) * 100).toFixed(1);
    },

    refundRate() {
      if (!this.summaryData || this.summaryData.total_sales === 0) return 0;
      return ((this.summaryData.total_refund / this.summaryData.total_sales) * 100).toFixed(1);
    },

    locationOptions() {
      if (!this.packet.locations || this.packet.locations.length === 0) return [];
      return this.packet.locations.map(loc => ({ value: loc.value, text: loc.text }));
    },

    salespersonOptions() {
      return Object.keys(this.salespersonSummary).map(name => ({ value: name, text: name }));
    },

    totalLocationRegularSales() {
      return Object.values(this.locationSummary).reduce((sum, loc) => sum + (loc.regular_sales || 0), 0);
    },

    totalLocationLayawayTotal() {
      return Object.values(this.locationSummary).reduce((sum, loc) => sum + (loc.layaway_total || 0), 0);
    },

    totalLocationLayawayPaid() {
      return Object.values(this.locationSummary).reduce((sum, loc) => sum + (loc.layaway_paid || 0), 0);
    },

    totalLocationRefunds() {
      return Object.values(this.locationSummary).reduce((sum, loc) => sum + (loc.refund || 0), 0);
    },

    totalLocationNet() {
      return Object.values(this.locationSummary).reduce((sum, loc) => sum + (loc.net || 0), 0);
    },

    totalLocationOrders() {
      return Object.values(this.locationSummary).reduce((sum, loc) => sum + (loc.count || 0), 0);
    },

    totalSalespersonRegularSales() {
      return Object.values(this.salespersonSummary).reduce((sum, sp) => sum + (sp.regular_sales || 0), 0);
    },

    totalSalespersonLayawayTotal() {
      return Object.values(this.salespersonSummary).reduce((sum, sp) => sum + (sp.layaway_total || 0), 0);
    },

    totalSalespersonLayawayPaid() {
      return Object.values(this.salespersonSummary).reduce((sum, sp) => sum + (sp.layaway_paid || 0), 0);
    },

    totalSalespersonRefunds() {
      return Object.values(this.salespersonSummary).reduce((sum, sp) => sum + (sp.refund || 0), 0);
    },

    totalSalespersonNet() {
      return Object.values(this.salespersonSummary).reduce((sum, sp) => sum + (sp.net || 0), 0);
    },

    totalSalespersonOrders() {
      return Object.values(this.salespersonSummary).reduce((sum, sp) => sum + (sp.count || 0), 0);
    },

    totalSalespersonAvgOrder() {
      const totalNet = this.totalSalespersonNet;
      const totalOrders = this.totalSalespersonOrders;
      if (totalOrders === 0) return 0;
      return totalNet / totalOrders;
    },

    filteredDetailedData() {
      let filtered = [...this.detailedData];

      if (this.selectedLocation) {
        filtered = filtered.filter(item => item.location_id == this.selectedLocation);
      }
      if (this.selectedSalesperson) {
        filtered = filtered.filter(item => item.username === this.selectedSalesperson);
      }
      if (this.selectedSaleType) {
        filtered = filtered.filter(item => {
          if (this.selectedSaleType === 'refund') return item.total_dollar_amount < 0;
          if (this.selectedSaleType === 'layaway') return item.sale_type === 'layaway';
          if (this.selectedSaleType === 'regular') return item.sale_type !== 'layaway' && item.total_dollar_amount >= 0;
          return true;
        });
      }

      if (this.showWarrantyWithDiscount) {
        filtered = filtered.filter(item =>
          (item.warranty_charges || 0) > 0 &&
          (item.total_discount || 0) > 0
        );
      }

      if (this.detailedSortKey) {
        filtered = filtered.sort((a, b) => {
          let aVal = a[this.detailedSortKey] || 0;
          let bVal = b[this.detailedSortKey] || 0;
          if (this.detailedSortKey === 'ts') {
            aVal = moment(a.ts).valueOf();
            bVal = moment(b.ts).valueOf();
          }
          if (typeof aVal === 'string') {
            return this.detailedSortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
          }
          return this.detailedSortAsc ? aVal - bVal : bVal - aVal;
        });
      }

      return filtered;
    },

    paginatedDetailedData() {
      const start = (this.detailedPage - 1) * this.detailedPageSize;
      return this.filteredDetailedData.slice(start, start + this.detailedPageSize);
    },

    /* ---------- TOTALS (using adjusted commission functions) ---------- */
    totalDetailedOrderTotal() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.order_total || 0), 0);
    },
    totalDetailedBaseCommission() {
      return this.filteredDetailedData.reduce((s, i) => s + this.adjustedBaseCommission(i), 0);
    },
    totalDetailedExtraCharges() {
      return this.filteredDetailedData.reduce((s, i) => s + this.extraCharges(i), 0);
    },
    totalDetailedSales() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.total_dollar_amount || 0), 0);
    },
    totalDetailedTax() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.sales_tax || 0), 0);
    },
    totalDetailedDiscount() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.total_discount || 0), 0);
    },
    totalDetailedDiscountPercent() {
      const sold = this.totalDetailedOrderTotal;
      const disc = this.totalDetailedDiscount;
      const original = sold + disc;
      if (original <= 0) return 0;
      return (disc / original) * 100;
    },
    totalDetailedWarranty() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.warranty_charges || 0), 0);
    },
    totalDetailedWarrantyComm() {
      return this.filteredDetailedData.reduce((s, i) => s + this.adjustedWarrantyCommission(i), 0);
    },
    totalDetailedSetup() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.setup_charges || 0), 0);
    },
    totalDetailedSetupComm() {
      return this.filteredDetailedData.reduce((s, i) => s + this.adjustedSetupCommission(i), 0);
    },
    totalDetailedDelivery() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.delivery_charges || 0), 0);
    },
    totalDetailedDeliveryComm() {
      return this.filteredDetailedData.reduce((s, i) => s + this.adjustedDeliveryCommission(i), 0);
    },
    totalDetailedSpiff() {
      return this.filteredDetailedData.reduce((s, i) => s + (i.spiff_commission || 0), 0);
    },
    totalDetailedTotalCommission() {
      return this.filteredDetailedData.reduce((s, i) => s + this.adjustedTotalCommission(i), 0);
    }
  },
  watch: {
    selectedLocation() {
      this.detailedPage = 1;
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) {
        this.fetchDetailedData();
      }
    },
    selectedSalesperson() {
      this.detailedPage = 1;
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) {
        this.fetchDetailedData();
      }
    },
    selectedSaleType() {
      this.detailedPage = 1;
    },
    showWarrantyWithDiscount() {
      this.detailedPage = 1;
    }
  },
  mounted() {
    this.initializeDashboard();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.destroyCharts();
  },
  created() {
    this.loadUserData();
  },
  methods: {
    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return value.toLocaleString();
    },

    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return `$${parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },

    formatDateTime(ts) {
      if (!ts) return '-';
      return moment(ts).format('MM-DD-YYYY HH:mm:ss A');
    },

    getLocationName(locationId) {
      if (!this.packet.locations) return `Location ${locationId}`;
      const location = this.packet.locations.find(l => l.value == locationId);
      return location ? location.text : `Location ${locationId}`;
    },

    /* ==========================================================
       OPEN INVOICE IN NEW TAB (uses Vue Router base URL)
       ========================================================== */
    openInvoice(invoiceId) {
      if (!invoiceId) return;
      const routeData = this.$router.resolve({
        path: `/sales/viewsale/${invoiceId}/`
      });
      window.open(routeData.href, '_blank', 'noopener,noreferrer');
    },

    calculateDiscountPercent(item) {
      const amount = item.order_total || 0;
      const discount = item.total_discount || 0;
      if (amount <= 0 && discount <= 0) return 0;
      const original = amount ;
      if (original <= 0) return 0;
      return (discount / original) * 100;
    },

    /* ==========================================================
       EXTRA CHARGES = Warranty + Setup + Delivery
       ========================================================== */
    extraCharges(item) {
      return (item.warranty_charges || 0) +
             (item.setup_charges || 0) +
             (item.delivery_charges || 0);
    },

    /* ==========================================================
       COMMISSION ADJUSTMENT RULES
       ----------------------------------------------------------
       1) Base commission is HALVED if discount% > 5
       2) Warranty commission is ALWAYS HALVED
       3) Delivery + Setup commission = 0 if (delivery + setup) < 100
          otherwise normal
       4) Spiff is unchanged
       ========================================================== */
    adjustedBaseCommission(item) {
      const base = item.commission || 0;
      const discPct = this.calculateDiscountPercent(item);
      return discPct > 5 ? base / 2 : base;
    },

   adjustedWarrantyCommission(item) {
    const wc = item.warranty_commission || 0;
    const combined = (item.delivery_charges || 0) + (item.setup_charges || 0);
    return combined < 100 ? wc / 2 : wc;
  },

    adjustedDeliveryCommission(item) {
      const dc = item.delivery_commission || 0;
      const setup = item.setup_charges || 0;
      const delivery = item.delivery_charges || 0;
      const combined = setup + delivery;
      if (combined < 100) return 0;
      return dc;
    },

    adjustedSetupCommission(item) {
      const sc = item.setup_commission || 0;
      const setup = item.setup_charges || 0;
      const delivery = item.delivery_charges || 0;
      const combined = setup + delivery;
      if (combined < 100) return 0;
      return sc;
    },

    adjustedTotalCommission(item) {
      return this.adjustedBaseCommission(item) +
             this.adjustedWarrantyCommission(item) +
             this.adjustedDeliveryCommission(item) +
             this.adjustedSetupCommission(item) +
             (item.spiff_commission || 0);
    },

    getRowBackgroundColor(item) {
      if (item.total_dollar_amount < 0) return '#ffab91';
      if (this.calculateDiscountPercent(item) > 5) return '#ffcccc';
      return 'white';
    },

    sortDetailedBy(key) {
      if (this.detailedSortKey === key) {
        this.detailedSortAsc = !this.detailedSortAsc;
      } else {
        this.detailedSortKey = key;
        this.detailedSortAsc = true;
      }
      this.detailedPage = 1;
    },

    onViewModeChange() {
      if (this.viewMode === 'detailed') {
        this.fetchDetailedDataForAll();
      }
    },

    async fetchDetailedDataForAll() {
      if (!this.date_range || this.date_range.length < 2) {
        this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
        return;
      }

      this.isLoadingDetailed = true;
      const data = {
        username: this.packet.userDetails.username,
        dates: this.date_range,
        location_id: null,
        salesperson: null,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };

      try {
        const response = await axios.post("/api/reports/reports_v5/", data, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.data.result && !response.data.error) {
          this.detailedData = response.data.result.map(item => ({
            ...item,
            payment_methods: item.payment_methods || []
          }));
          this.detailedPage = 1;
          this.$bvToast.toast(`Loaded ${this.detailedData.length} detailed transactions`,
            { title: 'Success', variant: 'success', autoHideDelay: 2000 });
        } else {
          this.$bvToast.toast(response.data.error || 'No data returned', { title: 'Info', variant: 'info' });
          this.detailedData = [];
        }
      } catch (error) {
        console.error('API Error:', error);
        this.$bvToast.toast('Error fetching detailed data', { title: 'Error', variant: 'danger' });
        this.detailedData = [];
      } finally {
        this.isLoadingDetailed = false;
      }
    },

    selectLocationFromTable(locationId) {
      if (this.selectedLocation === locationId) {
        this.selectedLocation = null;
      } else {
        this.selectedLocation = locationId;
        this.selectedSalesperson = null;
      }
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) {
        this.fetchDetailedData();
      }
    },

    selectSalespersonFromTable(salesperson) {
      if (this.selectedSalesperson === salesperson) {
        this.selectedSalesperson = null;
      } else {
        this.selectedSalesperson = salesperson;
        this.selectedLocation = null;
      }
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) {
        this.fetchDetailedData();
      }
    },

    clearFilters() {
      this.selectedLocation = null;
      this.selectedSalesperson = null;
      this.selectedSaleType = null;
      this.showWarrantyWithDiscount = false;
      this.detailedData = [];
      this.fetchSummaryData();
    },

    loadUserData() {
      try {
        this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const locations = localStorage.getItem('locations');
        if (locations) this.packet.locations = JSON.parse(locations);
        const packet = JSON.parse(localStorage.getItem('packet') || '{}');
        if (packet.locations && packet.locations.length) this.packet.locations = packet.locations;
      } catch (e) {
        console.error('Error loading user data:', e);
      }
    },

    initializeDashboard() {
      const today = moment();
      this.date_range = [
        today.startOf('month').format('MM-DD-YYYY'),
        today.endOf('month').format('MM-DD-YYYY')
      ];
      this.picked_date_range = 'this_month';
      this.fetchSummaryData();
    },

    set_dates(value) {
      let start = null, end = null;
      let today = moment();
      switch (value) {
        case 'today': start = end = today.format('MM-DD-YYYY'); break;
        case 'yesterday': start = end = today.subtract(1, 'days').format('MM-DD-YYYY'); break;
        case 'this_week': start = today.startOf('week').format('MM-DD-YYYY'); end = today.endOf('week').format('MM-DD-YYYY'); break;
        case 'last_week': start = today.subtract(1, 'week').startOf('week').format('MM-DD-YYYY'); end = today.subtract(1, 'week').endOf('week').format('MM-DD-YYYY'); break;
        case 'this_month': start = today.startOf('month').format('MM-DD-YYYY'); end = today.endOf('month').format('MM-DD-YYYY'); break;
        case 'last_month': start = today.subtract(1, 'month').startOf('month').format('MM-DD-YYYY'); end = today.subtract(1, 'month').endOf('month').format('MM-DD-YYYY'); break;
      }
      if (start && end) {
        this.date_range = [start, end];
        this.picked_date_range = value;
        this.fetchSummaryData();
      }
    },

    refreshDetailedData() {
      this.fetchDetailedData();
    },

    async fetchSummaryData() {
      if (!this.date_range || this.date_range.length < 2) {
        this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
        return;
      }

      this.isLoading = true;
      const data = {
        username: this.packet.userDetails.username,
        dates: this.date_range,
        location_id: this.selectedLocation,
        salesperson: this.selectedSalesperson,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };

      try {
        const response = await axios.post("/api/reports/reports_optimized/", data, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.data.result && !response.data.error) {
          const result = response.data.result;
          this.summaryData = result.summary || {};
          this.locationSummary = result.location_summary || {};
          this.salespersonSummary = result.salesperson_summary || {};
          this.dailyTrend = result.daily_trend || {};

          this.$nextTick(() => {
            this.renderDailyTrendChart();
            this.renderLocationChart();
          });

          this.$bvToast.toast(`Loaded ${this.summaryData.total_orders || 0} transactions`,
            { title: 'Success', variant: 'success', autoHideDelay: 2000 });
        } else {
          this.$bvToast.toast(response.data.error || 'No data returned', { title: 'Info', variant: 'info' });
        }
      } catch (error) {
        console.error('API Error:', error);
        this.$bvToast.toast('Error fetching data', { title: 'Error', variant: 'danger' });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchDetailedData() {
      if (!this.date_range || this.date_range.length < 2) {
        this.$bvToast.toast('Please select a date range', { title: 'Warning', variant: 'warning' });
        return;
      }

      if (!this.selectedLocation && !this.selectedSalesperson) {
        this.detailedData = [];
        return;
      }

      this.isLoadingDetailed = true;
      const data = {
        username: this.packet.userDetails.username,
        dates: this.date_range,
        location_id: this.selectedLocation,
        salesperson: this.selectedSalesperson,
        current_user_id: this.packet.userDetails.id,
        userid: this.packet.userDetails.id
      };

      try {
        const response = await axios.post("/api/reports/reports_v5/", data, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.data.result && !response.data.error) {
          this.detailedData = response.data.result.map(item => ({
            ...item,
            payment_methods: item.payment_methods || []
          }));
          this.detailedPage = 1;
          this.$bvToast.toast(`Loaded ${this.detailedData.length} detailed transactions`,
            { title: 'Success', variant: 'success', autoHideDelay: 2000 });
        } else {
          this.$bvToast.toast(response.data.error || 'No data returned', { title: 'Info', variant: 'info' });
          this.detailedData = [];
        }
      } catch (error) {
        console.error('API Error:', error);
        this.$bvToast.toast('Error fetching detailed data', { title: 'Error', variant: 'danger' });
        this.detailedData = [];
      } finally {
        this.isLoadingDetailed = false;
      }
    },

    onLocationChange() {
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) {
        this.fetchDetailedData();
      }
    },

    onSalespersonChange() {
      this.fetchSummaryData();
      if (this.viewMode === 'detailed' && (this.selectedLocation || this.selectedSalesperson)) {
        this.fetchDetailedData();
      }
    },

    onFilterChange() {
      this.detailedPage = 1;
    },

    renderDailyTrendChart() {
      const ctx = this.$refs.dailyTrendChart;
      if (!ctx || !this.dailyTrend || Object.keys(this.dailyTrend).length === 0) return;
      if (this.dailyTrendChart) this.dailyTrendChart.destroy();

      const sortedDates = Object.keys(this.dailyTrend).sort();
      this.dailyTrendChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: sortedDates.map(d => moment(d).format('MM-DD')),
          datasets: [
            { label: 'Sales', data: sortedDates.map(d => this.dailyTrend[d].sales || 0), borderColor: this.chartColors.success, backgroundColor: 'rgba(16,185,129,0.1)', tension: 0.3, fill: true, borderWidth: 3 },
            { label: 'Refunds', data: sortedDates.map(d => this.dailyTrend[d].refunds || 0), borderColor: this.chartColors.danger, backgroundColor: 'rgba(239,68,68,0.05)', tension: 0.3, fill: true, borderWidth: 3, borderDash: [5,5] }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: { label: (ctx) => `${ctx.dataset.label}: $${ctx.raw.toLocaleString()}` },
              bodyFont: { size: 16 },
              titleFont: { size: 16 }
            },
            legend: { labels: { font: { size: 16 } } }
          },
          scales: {
            y: { ticks: { callback: v => `$${v.toLocaleString()}`, font: { size: 14 } } },
            x: { ticks: { font: { size: 14 } } }
          }
        }
      });
    },

    renderLocationChart() {
      const ctx = this.$refs.locationChart;
      if (!ctx || !this.locationSummary || Object.keys(this.locationSummary).length === 0) return;
      if (this.locationChart) this.locationChart.destroy();

      const locations = Object.keys(this.locationSummary);
      this.locationChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: locations.map(l => this.getLocationName(l)),
          datasets: [{ data: locations.map(l => (this.locationSummary[l].net || 0)), backgroundColor: [this.chartColors.primary, this.chartColors.success, this.chartColors.warning, this.chartColors.info], borderWidth: 0 }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { font: { size: 14 } } },
            tooltip: {
              callbacks: { label: (ctx) => `${ctx.label}: $${ctx.raw.toLocaleString()}` },
              bodyFont: { size: 16 },
              titleFont: { size: 16 }
            }
          },
          cutout: '60%'
        }
      });
    },

    exportToCSV() {
      if (this.filteredDetailedData.length === 0) {
        this.$bvToast.toast('No data to export', { title: 'Info', variant: 'info' });
        return;
      }

      const headers = [
        'Date','Sold From','Invoice #','Sold By',
        'Order Total','Discount','Disc %','Commission','Extra Charges',
        'Sales Tax','Total Sold',
        'Warranty','Warranty Comm','Delivery','Delivery Comm',
        'Setup','Setup Comm','Spiff','Total Commission'
      ];

      const rows = this.filteredDetailedData.map(item => [
        item.ts,
        this.getLocationName(item.location_id),
        item.invoice_id,
        item.username,
        (item.order_total || 0).toFixed(2),
        (item.total_discount || 0).toFixed(2),
        this.calculateDiscountPercent(item).toFixed(2) + '%',
        this.adjustedBaseCommission(item).toFixed(2),
        this.extraCharges(item).toFixed(2),
        (item.sales_tax || 0).toFixed(2),
        (item.total_dollar_amount || 0).toFixed(2),
        (item.warranty_charges || 0).toFixed(2),
        this.adjustedWarrantyCommission(item).toFixed(2),
        (item.delivery_charges || 0).toFixed(2),
        this.adjustedDeliveryCommission(item).toFixed(2),
        (item.setup_charges || 0).toFixed(2),
        this.adjustedSetupCommission(item).toFixed(2),
        (item.spiff_commission || 0).toFixed(2),
        this.adjustedTotalCommission(item).toFixed(2)
      ]);

      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_report_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.$bvToast.toast(`Exported ${this.filteredDetailedData.length} transactions`, { title: 'Success', variant: 'success', autoHideDelay: 1500 });
    },

    handleResize() {
      setTimeout(() => {
        this.renderDailyTrendChart();
        this.renderLocationChart();
      }, 100);
    },

    destroyCharts() {
      ['dailyTrendChart', 'locationChart'].forEach(chart => {
        if (this[chart]) { this[chart].destroy(); this[chart] = null; }
      });
    }
  }
};
</script>

<style scoped>
/* =========================================================
   GLOBAL
   ========================================================= */
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 24px;
}

/* =========================================================
   HEADER
   ========================================================= */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
}

.header-icon-wrapper {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  font-size: 28px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.header-title {
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 16px;
  opacity: 0.8;
}

/* =========================================================
   FILTERS
   ========================================================= */
.filters-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: white;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 18px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
}

.quick-date-btn {
  border-radius: 20px;
  font-size: 16px;
  padding: 10px 18px;
  font-weight: 500;
}

.filter-select {
  border-radius: 12px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 18px;
  padding: 10px 16px;
}

.search-btn {
  border-radius: 12px;
  padding: 12px 36px;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.5px;
}

.advanced-toggle {
  font-size: 16px;
  font-weight: 500;
}

.filter-alert {
  font-size: 16px;
  border-radius: 12px;
}

.clear-filter-btn {
  font-size: 16px;
}

/* =========================================================
   KPI CARDS
   ========================================================= */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

.kpi-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 20px 26px;
  border: 1px solid #e8edf3;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.kpi-card:hover {
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.kpi-card.dark {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 8px 26px rgba(11, 18, 32, 0.35);
}

.kpi-card.dark .kpi-label { color: rgba(255, 255, 255, 0.65); }
.kpi-card.dark .kpi-value { color: #ffffff; }

.accent-green::before   { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-blue::before    { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
.accent-purple::before  { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.accent-amber::before   { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.accent-red::before     { background: linear-gradient(90deg, #ef4444, #f87171); }
.accent-emerald::before { background: linear-gradient(90deg, #10b981, #34d399); }

.kpi-icon-wrap {
  width: 68px;
  height: 68px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 30px;
  margin-bottom: 20px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.icon-green   { background: linear-gradient(135deg, #10b981, #34d399); }
.icon-blue    { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.icon-purple  { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.icon-amber   { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.icon-red     { background: linear-gradient(135deg, #ef4444, #f87171); }
.icon-emerald { background: linear-gradient(135deg, #10b981, #34d399); }

.kpi-label {
  font-size: 16px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 10px;
  letter-spacing: 0.2px;
}

.kpi-value {
  font-size: 38px;
  font-weight: 800;
  color: #0b1220;
  line-height: 1.1;
  letter-spacing: -1px;
  margin-bottom: 16px;
  word-break: break-word;
}

.kpi-trend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 999px;
  white-space: nowrap;
}

.trend-green   { color: #047857; background: #d1fae5; }
.trend-blue    { color: #1d4ed8; background: #dbeafe; }
.trend-purple  { color: #6d28d9; background: #ede9fe; }
.trend-amber   { color: #92400e; background: #fef3c7; }
.trend-red     { color: #b91c1c; background: #fee2e2; }
.trend-emerald { color: #047857; background: #d1fae5; }

.kpi-card.dark .kpi-trend.trend-green {
  color: #34d399;
  background: rgba(16, 185, 129, 0.18);
}

/* =========================================================
   CHARTS
   ========================================================= */
.chart-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chart-header-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.chart-title { font-size: 20px; font-weight: 600; }

/* =========================================================
   TABLES
   ========================================================= */
.data-table-card {
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-title { font-size: 20px; font-weight: 600; }
.table-badge { font-size: 14px; padding: 6px 12px; }

.table-wrapper {
  overflow-x: auto;
  max-height: 650px;
}

.table-wrapper-sm { overflow-x: auto; }

.compact-table, .sales-table {
  width: 100%;
  font-size: 14px;
  border-collapse: collapse;
}

.compact-table th, .compact-table td,
.sales-table th, .sales-table td {
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  font-size: 14px;
}

.compact-table th, .sales-table th {
  background: #c3cdd3;
  font-weight: 700;
  position: sticky;
  top: 0;
  white-space: nowrap;
  cursor: pointer;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.sales-table td { white-space: nowrap; }

.compact-table tbody tr, .sales-table tbody tr {
  cursor: pointer;
  transition: background 0.2s;
}

.compact-table tbody tr:hover, .sales-table tbody tr:hover {
  background: #e0e7ff !important;
}

.highlight-row {
  background: #dbeafe !important;
  border-left: 5px solid #6366f1;
}

.header {
  font-weight: bold;
  background-color: lightyellow;
  font-size: 16px;
}

.non-header { font-weight: normal; font-size: 16px; }

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
  padding: 16px;
}

/* =========================================================
   COMPACT DETAILED TABLE — ALL COLUMNS FIT, NO H-SCROLL
   ========================================================= */
.compact-detail-wrapper {
  width: 100%;
  overflow-x: hidden;
}

.compact-sales-table {
  width: 100%;
  table-layout: fixed;
  font-size: 13px;
}

.compact-sales-table th,
.compact-sales-table td {
  padding: 6px 4px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  border: 1px solid #dee2e6;
}

.compact-sales-table th {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  padding: 8px 3px;
  line-height: 1.15;
}

.compact-sales-table td.text-right { text-align: right; }
.compact-sales-table th.text-right { text-align: right; }

/* Column widths (percentage based so all 19 columns fit on screen) */
.compact-sales-table .col-date    { width: 8.5%; }
.compact-sales-table .col-loc     { width: 6%; }
.compact-sales-table .col-inv     { width: 6%; }
.compact-sales-table .col-by      { width: 6%; }
.compact-sales-table .col-num     { width: 4%; }
.compact-sales-table .col-num-sm  { width: 3.5%; }

.compact-detail-wrapper ::-webkit-scrollbar { display: none; }

/* =========================================================
   INVOICE LINK
   ========================================================= */
.invoice-link {
  color: #2563eb;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
}

.invoice-link:hover {
  color: #ffffff;
  background-color: #2563eb;
  text-decoration: none;
}

.compact-sales-table .invoice-link {
  font-size: 12px;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */
@media (max-width: 1600px) {
  .kpi-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 992px) {
  .dashboard-container { padding: 16px; }
  .header-title { font-size: 24px; }
  .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .kpi-value { font-size: 30px; }
  .kpi-icon-wrap { width: 56px; height: 56px; font-size: 24px; margin-bottom: 14px; }
  .kpi-card { padding: 24px 16px 20px; }
  .compact-table th, .compact-table td { padding: 10px 12px; font-size: 14px; }
  .chart-title { font-size: 16px; }
  .table-title { font-size: 16px; }
  .compact-sales-table th,
  .compact-sales-table td { font-size: 10px; padding: 5px 2px; }
  .compact-sales-table th { font-size: 9px; }
}

@media (max-width: 576px) {
  .dashboard-container { padding: 10px; }
  .kpi-grid { grid-template-columns: 1fr; }
  .kpi-value { font-size: 26px; }
  .kpi-label { font-size: 14px; }
  .kpi-trend { font-size: 12px; padding: 4px 10px; }
  .compact-table th, .compact-table td { padding: 8px 10px; font-size: 12px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsMySales.vue